// @givmo/fundraisers-module — what the assistant's last change ADDED, washed in the preview (EMC-WEB10;
// mockups 14c and 14d: the new words of a changed paragraph washed, a block that wasn't there before
// outlined, a one-word change washed alone).
//
// The preview is the server's render, so the wash is drawn over THAT document, never a render of our own:
// the words of the email before the change (its previous render) are aligned against the words of the
// render on screen (a longest common subsequence over the two word sequences), and every word on screen
// that the alignment did not keep is new.
//
//   · A block whose words are ALL new, three or more of them, is a block the change added: its cell is
//     outlined, not washed word by word (14c's employer-match callout). A shorter one is washed: a greeting
//     whose word and fallback both changed ("Hi there," to "Dear friend,", 14d) is the same block changed. The renderer draws each top-level block in its own `td.emc-px`
//     row (`org_email_campaign_render.py`, `_Render.row`), which is what names a block here.
//   · A block changed in two or more places is washed whole (14c's shortened paragraph): word-by-word
//     there it reads as confetti. A block changed in one place has that run washed (14d's "Dear").
//   · An image whose address the previous render didn't hold is outlined.
//
// Nothing is washed for an email that had no blocks before (14b's first draft is all new, and the frame
// draws it plain): the caller passes no previous render then.

/** The wash's colours: the mockups' warm highlight and its outline. */
const WASH_BACKGROUND = '#fdecb0';
const OUTLINE = '2px solid #e8b10a';

/** The fewest words a wholly new block has for it to read as added (outlined) rather than changed. */
const MIN_ADDED_BLOCK_WORDS = 3;

/** The renderer's class on each top-level block's cell. */
const BLOCK_CELL = 'td.emc-px';

interface Word {
  node: Text;
  start: number;
  end: number;
  value: string;
}

function textNodes(root: Node): Text[] {
  const doc = root.ownerDocument ?? (root as Document);
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const parent = node.parentElement;
    if (parent !== null && /^(STYLE|SCRIPT|TITLE)$/.test(parent.tagName)) continue;
    nodes.push(node as Text);
  }
  return nodes;
}

function wordsIn(root: Node): Word[] {
  const words: Word[] = [];
  for (const node of textNodes(root)) {
    const pattern = /\S+/g;
    const value = node.data;
    for (let match = pattern.exec(value); match !== null; match = pattern.exec(value)) {
      words.push({ node, start: match.index, end: match.index + match[0].length, value: match[0] });
    }
  }
  return words;
}

/** The words of an HTML string, in document order. */
export function htmlWords(html: string): string[] {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return wordsIn(parsed.body).map((word) => word.value);
}

/** The image addresses of an HTML string. */
function htmlImages(html: string): Set<string> {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return new Set(
    Array.from(parsed.querySelectorAll('img'), (image) => image.getAttribute('src') ?? ''),
  );
}

/**
 * For each word of `after`, whether a longest common subsequence with `before` keeps it. Emails are a few
 * hundred words, so the quadratic table is small; past a bound the change is treated as all new.
 */
export function keptWords(before: readonly string[], after: readonly string[]): boolean[] {
  const n = before.length;
  const m = after.length;
  if (n * m > 4_000_000) return after.map(() => false);
  const table: Uint16Array[] = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i]![j] =
        before[i] === after[j]
          ? table[i + 1]![j + 1]! + 1
          : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const kept = after.map(() => false);
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      kept[j] = true;
      i += 1;
      j += 1;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return kept;
}

function outline(element: HTMLElement): void {
  element.style.outline = OUTLINE;
  element.style.outlineOffset = '2px';
  element.style.borderRadius = '4px';
  element.setAttribute('data-designer-added', 'block');
}

/**
 * Outline an added block at its content's width, never its cell's: the renderer's block cell spans the
 * email's whole width with its side padding inside, so its outline stood wider than the email's text
 * and callouts (the polish-1 user test, 14c). A cell holding one element outlines that element; one
 * holding loose text outlines itself, drawn inside its own edge.
 */
function outlineBlock(cell: HTMLElement): void {
  const only = cell.children.length === 1 ? (cell.firstElementChild as HTMLElement | null) : null;
  if (only !== null) {
    outline(only);
    return;
  }
  outline(cell);
  cell.style.outlineOffset = '-2px';
}

/** Wrap each run of new words inside one text node in a wash. */
function washRuns(doc: Document, runs: Map<Text, Array<[number, number]>>): number {
  let washed = 0;
  for (const [node, spans] of runs) {
    // Merge spans that only whitespace separates, so a new phrase is one wash.
    const merged: Array<[number, number]> = [];
    for (const span of spans) {
      const last = merged.at(-1);
      if (last !== undefined && node.data.slice(last[1], span[0]).trim() === '') last[1] = span[1];
      else merged.push([span[0], span[1]]);
    }
    // Right to left, so earlier offsets stay true while the node is split.
    for (let index = merged.length - 1; index >= 0; index -= 1) {
      const [start, end] = merged[index]!;
      const range = doc.createRange();
      range.setStart(node, start);
      range.setEnd(node, end);
      const mark = doc.createElement('mark');
      mark.setAttribute('data-designer-added', 'words');
      mark.style.background = WASH_BACKGROUND;
      mark.style.color = 'inherit';
      mark.style.borderRadius = '2px';
      mark.style.padding = '0 1px';
      range.surroundContents(mark);
      washed += 1;
    }
  }
  return washed;
}

/**
 * Wash what's new in `doc` (the render on screen) against `previousHtml` (the render before the change).
 * Returns how many washes and outlines it drew.
 */
export function washChanges(
  doc: Document,
  previousHtml: string,
): { washed: number; outlined: number } {
  const before = htmlWords(previousHtml);
  const words = wordsIn(doc.body);
  const kept = keptWords(
    before,
    words.map((word) => word.value),
  );

  // Group each block cell's words, to find the blocks that are wholly new.
  const cells = new Map<Element, { total: number; added: number }>();
  words.forEach((word, index) => {
    const cell = word.node.parentElement?.closest(BLOCK_CELL);
    if (!cell) return;
    const tally = cells.get(cell) ?? { total: 0, added: 0 };
    tally.total += 1;
    if (!kept[index]) tally.added += 1;
    cells.set(cell, tally);
  });
  const addedCells = new Set(
    Array.from(cells).flatMap(([cell, tally]) =>
      tally.total >= MIN_ADDED_BLOCK_WORDS && tally.added === tally.total ? [cell] : [],
    ),
  );

  // A block whose new words fall in two or more separate runs is washed whole.
  const runsIn = new Map<Element, number>();
  words.forEach((word, index) => {
    const cell = word.node.parentElement?.closest(BLOCK_CELL);
    if (!cell || kept[index]) return;
    if (index === 0 || kept[index - 1]) runsIn.set(cell, (runsIn.get(cell) ?? 0) + 1);
  });
  const rewritten = new Set(
    Array.from(runsIn).flatMap(([cell, runCount]) => (runCount >= 2 ? [cell] : [])),
  );

  const runs = new Map<Text, Array<[number, number]>>();
  words.forEach((word, index) => {
    const home = word.node.parentElement?.closest(BLOCK_CELL);
    if (kept[index] && !(home && rewritten.has(home))) return;
    const cell = word.node.parentElement?.closest(BLOCK_CELL);
    if (cell && addedCells.has(cell)) return;
    const spans = runs.get(word.node) ?? [];
    spans.push([word.start, word.end]);
    runs.set(word.node, spans);
  });

  let outlined = 0;
  for (const cell of addedCells) {
    outlineBlock(cell as HTMLElement);
    outlined += 1;
  }
  const previousImages = htmlImages(previousHtml);
  for (const image of Array.from(doc.querySelectorAll('img'))) {
    const src = image.getAttribute('src') ?? '';
    if (previousImages.has(src)) continue;
    const cell = image.closest(BLOCK_CELL);
    if (cell && addedCells.has(cell)) continue;
    outline(image);
    outlined += 1;
  }
  return { washed: washRuns(doc, runs), outlined };
}
